import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Building2, User, Users, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRUPOS = [
  { key: "propiedades", label: "Propiedades", icon: Building2 },
  { key: "propietarios", label: "Propietarios", icon: Users },
  { key: "inquilinos", label: "Inquilinos", icon: User },
  { key: "contratos", label: "Contratos", icon: FileText },
];

export const GlobalSearch = ({ className = "" }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const r = await axios.get(`${API}/buscar`, {
          params: { q: query.trim() },
          headers: { Authorization: `Bearer ${token}` },
        });
        setResults(r.data);
        setOpen(true);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const goTo = (grupo, item) => {
    setOpen(false);
    setQuery("");
    if (grupo === "propiedades") navigate(`/propiedades/${item.id}`);
    else if (grupo === "propietarios") navigate(`/propietarios/${encodeURIComponent(item.email)}`);
    else if (grupo === "inquilinos" && item.property_id) navigate(`/propiedades/${item.property_id}`);
    else if (grupo === "contratos") navigate(`/contratos/${item.id}`);
  };

  const total = results ? GRUPOS.reduce((n, g) => n + (results[g.key]?.length || 0), 0) : 0;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder="Buscar propiedad, propietario, inquilino..."
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-colors"
          data-testid="global-search-input"
        />
      </div>

      {open && results && (
        <div
          className="absolute left-0 right-0 sm:right-auto sm:w-96 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-[60] max-h-[70vh] overflow-y-auto"
          data-testid="global-search-results"
        >
          {loading && <p className="px-4 py-2 text-xs text-gray-400">Buscando...</p>}
          {total === 0 && !loading ? (
            <p className="px-4 py-3 text-sm text-gray-500">Sin resultados para "{query}"</p>
          ) : (
            GRUPOS.map(({ key, label, icon: Icon }) =>
              results[key]?.length ? (
                <div key={key} className="py-1">
                  <p className="px-4 py-1 text-xs font-semibold uppercase text-gray-400">{label}</p>
                  {results[key].map((item, i) => (
                    <button
                      key={`${key}-${i}`}
                      onClick={() => goTo(key, item)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3"
                      data-testid={`search-result-${key}-${i}`}
                    >
                      <Icon className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 truncate">
                          {item.nombre || item.numero}
                        </span>
                        {item.detalle && <span className="block text-xs text-gray-500 truncate">{item.detalle}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null
            )
          )}
        </div>
      )}
    </div>
  );
};
